import React, { useEffect, useState } from "react";
import { Link, useParams, useOutletContext } from "react-router-dom";
import { ArrowLeft, Mail, MessageCircle, FileDown, Instagram, Facebook, Linkedin, Youtube } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { api, getMl, fileUrl } from "@/lib/api";

const ProductDetail = () => {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const { siteSettings } = useOutletContext() || {};
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => { setProduct(r.data); setActiveImg(0); });
  }, [id]);

  if (!product) return <div className="container-nakia py-32 text-center text-slate-500">Loading...</div>;

  const social = product.social_links || {};
  const fallbackSocial = siteSettings?.social_links || {};
  const wa = social.whatsapp || siteSettings?.contact?.whatsapp || fallbackSocial.whatsapp;
  const waText = encodeURIComponent(`Hi, I'd like to inquire about: ${getMl(product.name, lang)}`);
  const waLink = wa ? `https://wa.me/${wa.replace(/\D/g, "")}?text=${waText}` : null;

  const socialItems = [
    { key: "instagram", Icon: Instagram, url: social.instagram || fallbackSocial.instagram },
    { key: "facebook", Icon: Facebook, url: social.facebook || fallbackSocial.facebook },
    { key: "tiktok", Icon: () => <span className="font-bold">TT</span>, url: social.tiktok || fallbackSocial.tiktok },
    { key: "linkedin", Icon: Linkedin, url: social.linkedin || fallbackSocial.linkedin },
    { key: "youtube", Icon: Youtube, url: social.youtube || fallbackSocial.youtube },
    { key: "whatsapp", Icon: MessageCircle, url: waLink },
  ].filter((s) => s.url);

  const priceText = product.price_visible
    ? getMl(product.price_text, lang)
    : (getMl(product.price_text, lang) || t("product.bulkPrice"));

  return (
    <div data-testid="product-detail-page" className="bg-white">
      <div className="container-nakia py-10">
        <Link to={product.type === "export" ? "/export-commodities" : "/import-products"} data-testid="back-to-list" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-[#0A1128]">
          <ArrowLeft size={14} /> {t("common.backToList")}
        </Link>
      </div>

      <section className="container-nakia pb-20 grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Gallery */}
        <div>
          <div className="aspect-[4/3] bg-slate-100 overflow-hidden rounded-sm">
            {product.images?.[activeImg] ? (
              <img src={product.images[activeImg]} alt={getMl(product.name, lang)} className="w-full h-full object-cover" />
            ) : null}
          </div>
          {product.images?.length > 1 && (
            <div className="grid grid-cols-5 gap-3 mt-4">
              {product.images.map((img, idx) => (
                <button
                  key={idx}
                  data-testid={`gallery-thumb-${idx}`}
                  onClick={() => setActiveImg(idx)}
                  className={`aspect-square overflow-hidden rounded-sm border-2 ${
                    activeImg === idx ? "border-[#D4AF37]" : "border-transparent"
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-3">{product.origin_country}</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0A1128] leading-tight mb-5">
            {getMl(product.name, lang)}
          </h1>
          <p className="text-slate-600 leading-relaxed mb-7">{getMl(product.short_description, lang)}</p>

          {/* Price */}
          <div className="bg-[#0A1128] text-[#D4AF37] px-5 py-4 mb-7 rounded-sm">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-1">Price</div>
            <div className="font-display text-xl">{priceText}</div>
          </div>

          {/* Quick info grid */}
          <dl className="grid grid-cols-2 gap-x-6 gap-y-4 mb-7 text-sm">
            {[
              ["product.origin", product.origin_country],
              ["product.grade", product.grade],
              ["product.moq", product.moq],
              ["product.loadingPort", product.loading_port],
              ["product.monthlyCapacity", product.monthly_capacity],
              ["product.packaging", getMl(product.packaging, lang)],
            ].filter(([_, v]) => v).map(([key, val]) => (
              <div key={key}>
                <dt className="text-[10px] uppercase tracking-[0.25em] text-slate-500">{t(key)}</dt>
                <dd className="text-[#0A1128] font-medium">{val}</dd>
              </div>
            ))}
          </dl>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 mb-8">
            <Link to={`/contact?product=${product.id}`} data-testid="product-inquire-btn" className="btn-gold flex-1">
              <Mail size={14} /> {t("common.inquireNow")}
            </Link>
            {waLink && (
              <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="product-whatsapp-btn" className="btn-outline-navy flex-1">
                <MessageCircle size={14} /> {t("common.whatsappQuote")}
              </a>
            )}
            {product.catalogue_pdf && (
              <a href={fileUrl(product.catalogue_pdf)} target="_blank" rel="noopener noreferrer" data-testid="product-pdf-btn" className="btn-outline-navy flex-1">
                <FileDown size={14} /> PDF
              </a>
            )}
          </div>

          {/* Social */}
          {socialItems.length > 0 && (
            <div>
              <div className="text-eyebrow text-slate-500 mb-3">{t("product.shareOn")}</div>
              <div className="flex items-center gap-2">
                {socialItems.map(({ key, Icon, url }) => (
                  <a key={key} href={url} target="_blank" rel="noopener noreferrer" data-testid={`product-social-${key}`}
                     className="w-10 h-10 flex items-center justify-center border border-slate-200 hover:border-[#D4AF37] hover:text-[#D4AF37] text-slate-600 transition-colors rounded-sm">
                    <Icon size={16} />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Specifications */}
      {product.specifications?.length > 0 && (
        <section className="bg-[#F8FAFC] py-20">
          <div className="container-nakia max-w-3xl">
            <h2 className="font-display text-3xl text-[#0A1128] mb-8">{t("product.specifications")}</h2>
            <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <tbody>
                  {product.specifications.map((sp, i) => (
                    <tr key={i} className="border-b border-slate-100 last:border-0">
                      <td className="px-6 py-4 text-slate-500 uppercase text-xs tracking-[0.2em] w-1/3">{sp.key}</td>
                      <td className="px-6 py-4 text-[#0A1128] font-medium">{sp.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* Description */}
      {getMl(product.description, lang) && (
        <section className="py-20 bg-white">
          <div className="container-nakia max-w-3xl">
            <h2 className="font-display text-3xl text-[#0A1128] mb-6">Details</h2>
            <p className="text-slate-700 leading-relaxed whitespace-pre-line">{getMl(product.description, lang)}</p>
          </div>
        </section>
      )}
    </div>
  );
};

export default ProductDetail;
